import axios from 'axios'
import { pick } from 'lodash'
import railsService from '../../../utils/AxiosService'
import errorReporter from '@spectora/vue-error-service'
import { getReportContractorRecommendations } from '@/api'
import config from '@/utils/config.js'
import formatAndStoreReport from '@/utils/formatAndStoreReport'
import serializedV10SampleReportAdapter from '@/utils/serializedV10SampleReportAdapter'
import { signInWithCustomToken, subscribeToHermesJobById } from '@/db'
import {
  compileReport as hermesCompileReport,
  checkPayloadFreshness,
} from '@/services/hermesService'
const genericError = 'An error has occurred. Your changes likely did not save.'
const loadingError = "We're having trouble loading data. Please try again."

const firebaseProject = config('FIREBASE_PROJECT_ID') || 'spectora-x'
const firebaseFunctionRegion =
  config('FIREBASE_FUNCTION_REGION') || 'us-central1'
const cloudFunctionUrl = `https://${firebaseFunctionRegion}-${firebaseProject}.cloudfunctions.net`
const forcePayloadRebuild = config('FORCE_PAYLOAD_REBUILD')

const cloudFunction = (name, data) => {
  // eslint-disable-next-line no-console
  console.log(`calling ${name} with data: `, JSON.stringify(data, undefined, 2))
  return axios.post(`${cloudFunctionUrl}/${name}`, { data })
}

const FIRESTORE_PROCESSED_MEDIA = config('FIRESTORE_PROCESSED_MEDIA')
const RAILS_DOMAIN = config('RAILS_DOMAIN')

export const closeOverlay = ({ commit, dispatch, state }) => {
  commit('setValue', { key: 'overlayPhoto', value: null })
  commit('setValue', { key: 'currentPanoPhoto', value: null })
}

export const openComparisonViewer = ({ commit }, payload) => {
  commit('setValue', {
    key: 'comparisonViewer',
    value: {
      reinspectionPhotos: payload.reinspectionPhotos,
      originalPhotos: payload.originalPhotos,
      initialReinspectionIndex: payload.initialReinspectionIndex || 0,
      initialOriginalIndex: payload.initialOriginalIndex || 0,
    },
  })
}

export const closeComparisonViewer = ({ commit }) => {
  commit('setValue', { key: 'comparisonViewer', value: null })
}

export const fetchOriginalReportPhotos = async ({ commit, state }) => {
  const reportView = state.reportView
  if (!reportView || !reportView.attributes || !reportView.attributes.report_id)
    return

  const originalReportId = reportView.attributes.report_id
  try {
    const response = await axios.get(
      `${FIRESTORE_PROCESSED_MEDIA}/${originalReportId}.json?alt=media`
    )
    const report = response.data.report
    if (!report || !report.sections) return

    const photos = []
    report.sections.forEach((section) => {
      ;(section.items || []).forEach((item) => {
        ;(item.observations || []).forEach((observation) => {
          ;(observation.photos || []).forEach((photo) => {
            if (!photo.video_url) {
              photo.image = photo.edited_image_url || photo.original_image_url
            }
            photos.push({
              id: photo.id,
              type: 'observation_photo',
              attributes: photo,
            })
          })
        })
      })
    })

    commit('setValue', { key: 'originalReportPhotos', value: photos })
  } catch (error) {
    console.warn(
      '[fetchOriginalReportPhotos] Failed to fetch original report:',
      error
    )
  }
}

export const fetchAccount = ({ commit, dispatch, state }) => {
  return dispatch('fetchUserProfile').then((profile) => {
    if (
      profile.attributes &&
      profile.type !== 'admin' &&
      profile.attributes.company_id
    )
      return dispatch('fetchCompany', profile.attributes.company_id)
  })
}

export const createSampleReport = ({ commit, dispatch, state }, params) => {
  return new Promise((resolve, reject) => {
    railsService
      .post('/api/v1/sample_reports', params)
      .then((response) => {
        dispatch(
          'notifier/show',
          { type: 'Saved', msg: 'Sample Report Created!' },
          { root: true }
        )
        resolve(response.data.data)
      })
      .catch((error) => {
        commit('notifier/showError', genericError, { root: true })
        errorReporter.error(error)
        reject(error)
      })
  })
}

export const fetchCompany = ({ commit, dispatch, state }, companyId) => {
  return new Promise((resolve, reject) => {
    const prefix =
      state.loggedIn && state.userProfile.attributes.company_id ? '' : 'public/'
    railsService
      .get(`/api/v1/${prefix}companies/` + companyId)
      .then((response) => {
        commit('setValue', { key: 'company', value: response.data.data })
        resolve(response.data.data)
      })
      .catch((error) => {
        commit('notifier/showError', loadingError, { root: true })
        errorReporter.error(error)
        reject(error)
      })
  })
}

export const fetchRecommendedContractors = async (
  { commit, dispatch, state },
  reportId
) => {
  const resp = await getReportContractorRecommendations({
    report_id: reportId,
  })

  if (resp.data) {
    commit('setValue', { key: 'reportContractors', value: resp.data.data })
  }
}

export const fetchInspection = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    const url =
      state.loggedIn &&
      state.userProfile.attributes.company_id &&
      !state.sampleReport
        ? `inspections/${state.report.attributes.inspection_id}?include=inspection_attachments,buyer,reports,assignments&view=image,publish_recipients_with_sms_suppression`
        : `public/reports/${state.report.id}/inspections/${state.report.attributes.inspection_id}?include=inspectors,inspection_attachments,reports,inspection_reviews,assignments&view=image,publish_recipients_with_sms_suppression`
    railsService
      .get(`/api/v1/${url}`)
      .then((response) => {
        const insp = response.data.data
        const incs = response.data.included || []

        incs.forEach((inc) => {
          const key = inc.type + 's'
          if (!insp[key]) insp[key] = []
          insp[key].push(inc)
        })

        commit('setValue', { key: 'inspection', value: insp })
        resolve(insp)
      })
      .catch((error) => {
        commit('notifier/showError', loadingError, { root: true })
        errorReporter.error(error)
        reject(error)
      })
  })
}

/**
 * Lightweight fetch of company ID for unauthenticated users via public endpoints.
 * Used to initialize LaunchDarkly before ensureReportCompiled runs.
 * @param {Object} context - Vuex action context
 * @param {string} reportId - The report UUID
 * @returns {Promise<number|null>} The company ID, or null if fetch fails
 */
export const fetchReportCompanyId = async ({ commit }, reportId) => {
  try {
    const reportRes = await railsService.get(
      `/api/v1/public/reports/${reportId}`
    )
    const inspectionId = reportRes.data.data.attributes.inspection_id
    if (!inspectionId) return null

    const inspRes = await railsService.get(
      `/api/v1/public/reports/${reportId}/inspections/${inspectionId}`
    )
    const companyId = inspRes.data.data.attributes.company_id
    if (companyId) {
      commit('setCompanyId', companyId)
    }
    return companyId
  } catch (error) {
    // Non-fatal: if we can't fetch company ID, LD won't init and
    // ensureReportCompiled will fall through to legacy path
    // eslint-disable-next-line no-console
    console.warn('[fetchReportCompanyId] Failed:', error.message)
    return null
  }
}

export const fetchPdfStatus = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    railsService
      .get(`/api/v1/public/reports/${state.report.id}`)
      .then((response) => {
        const report = response.data.data

        if (!report.attributes.pdf_started_at) {
          state.report.attributes.pdf_started_at = null
          state.report.attributes.pdf_url = report.attributes.pdf_url
          state.report.attributes.summary_url = report.attributes.summary_url
          dispatch(
            'notifier/show',
            { type: 'Saved', msg: 'Your PDFs have been refreshed!' },
            { root: true }
          )
        }
      })
  })
}

export const fetchRecommendations = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    const companyId = state.inspection.attributes.company_id
    railsService
      .get(
        `/api/v1/public/recommendations?company_id=${companyId}&reporting_format=true`
      )
      .then((response) => {
        commit('setValue', {
          key: 'recommendations',
          value: response.data.data,
        })
        resolve(response.data.data)
      })
      .catch((error) => {
        commit('notifier/showError', loadingError, { root: true })
        errorReporter.error(error)
        reject(error)
      })
  })
}

const isValidFirebaseToken = (token) => {
  if (!token || typeof token !== 'string') return false
  return token.split('.').length === 3
}

export const fetchToken = async ({ commit }) => {
  const res = await axios.get(
    `https://${RAILS_DOMAIN}/api/v1/users/additional_creds`,
    { withCredentials: true }
  )
  const token = res.data.firestore_token
  if (token) {
    if (!isValidFirebaseToken(token)) {
      errorReporter.error(new Error('Invalid Firebase token format'), {
        context: {
          token_length: token.length,
          token_parts: token.split('.').length,
          project_id: firebaseProject,
          user_id: res.data.user_id,
          company_id: res.data.company_id,
        },
      })
      commit('setIsFirestoreAuthenticated', false)
    } else {
      try {
        await signInWithCustomToken(token)
        commit('setIsFirestoreAuthenticated', true)
      } catch (error) {
        errorReporter.error(error, {
          context: {
            token_length: token.length,
            project_id: firebaseProject,
            user_id: res.data.user_id,
            company_id: res.data.company_id,
          },
        })
        commit('setIsFirestoreAuthenticated', false)
      }
    }
  } else {
    commit('setIsFirestoreAuthenticated', false)
  }
  commit('setCompanyId', res.data.company_id)
  commit('setIsAdmin', res.data.is_admin)
  commit('setUserEmail', res.data.user_email)
  commit('setUserId', res.data.user_id)
}

export const fetchReportForSample = async ({ commit }, sampleReportId) => {
  const response = await axios.get(`
    https://${RAILS_DOMAIN}/api/v2/sample_reports/${sampleReportId}/report
  `)

  const result = await formatAndStoreReport(
    { commit },
    {
      report: serializedV10SampleReportAdapter(response.data.report),
      reportView: response.data.report_view,
    }
  )
  return result
}

export const fetchReportFirestore = async ({ commit }, reportId) => {
  let response
  try {
    response = await axios.get(
      `${FIRESTORE_PROCESSED_MEDIA}/${reportId}.json?alt=media`
    )
  } catch (error) {
    return false
  }

  commit('setValue', {
    key: 'reportBuiltAt',
    value: response.data.builtAt,
  })
  commit('setValue', {
    key: 'reportLastReceivedSync',
    value: response.data.lastReceivedSync,
  })

  const result = await formatAndStoreReport(
    { commit },
    { report: response.data.report, reportView: response.data.report_view }
  )
  return result
}

export const checkForReportUpdates = async (
  { commit, dispatch, state },
  reportId
) => {
  return (await cloudFunction('checkForReportUpdates', { id: reportId })).data
    .result
}

/**
 * Check if a report's GCS payload is fresh using Hermes public endpoint.
 * Used for unauthenticated users who cannot call the authenticated compile endpoint.
 * Non-fatal: if the check fails, we proceed to load from GCS cache anyway.
 * @param {Object} context - Vuex action context
 * @param {string} reportId - The report UUID
 * @returns {Promise<boolean>} true if payload is confirmed current
 */
export const checkReportPayloadFreshness = async (
  { commit, dispatch },
  reportId
) => {
  try {
    const result = await checkPayloadFreshness(reportId)
    return result.status === 'current'
  } catch (error) {
    if (error.statusCode === 404) {
      // 404 is expected for new/uncompiled reports — do not alert Sentry
      // eslint-disable-next-line no-console
      console.warn('[Hermes Public] Report not found:', reportId)
    } else {
      // eslint-disable-next-line no-console
      console.warn('[Hermes Public] Freshness check failed:', error.message)
      errorReporter.error(error)
    }
    // Non-fatal: proceed to load from GCS cache
    return false
  }
}

/**
 * Ensure a report is compiled and up-to-date before fetching
 * When Hermes is enabled, uses Hermes public freshness check for unauthenticated users,
 * or Hermes compile for authenticated users
 * When Hermes is disabled, falls back to legacy check + compile flow
 * @param {Object} context - Vuex action context
 * @param {Object} options
 * @param {string} options.reportId - The report UUID
 * @param {boolean} [options.isReinspection=false] - Whether this is a reinspection view
 */
export const ensureReportCompiled = async (
  { dispatch, state },
  { reportId, isReinspection = false }
) => {
  if (state.hermesActive) {
    if (!state.loggedIn) {
      // Unauthenticated user with Hermes enabled: use public freshness endpoint
      await dispatch('checkReportPayloadFreshness', reportId)
    } else {
      // Authenticated user with Hermes enabled: use full compile
      await dispatch('updateReportPayload', { reportId, clean: false })
    }
  } else {
    // Legacy: LD is ready but Hermes flag is off — check for updates first
    let hasUpdates = false
    try {
      hasUpdates = await dispatch('checkForReportUpdates', reportId)
    } catch (error) {
      // Legacy GCF failure is non-fatal: skip compile, load from existing cache
      // eslint-disable-next-line no-console
      console.warn(
        '[ensureReportCompiled] checkForReportUpdates failed:',
        error
      )
      errorReporter.error(error)
    }
    if (isReinspection || hasUpdates) {
      await dispatch('updateReportPayload', { reportId, clean: false })
    }
  }
}

/**
 * Update report payload by triggering compilation
 * Routes to Hermes when state.hermesActive is true, with fallback to legacy
 * @param {Object} context - Vuex action context
 * @param {Object} options - Compilation options
 * @param {string} options.reportId - The report UUID
 * @param {boolean} [options.clean=false] - Force full rebuild
 * @returns {Promise<Object>} Compilation response
 */
export const updateReportPayload = async (
  { commit, dispatch, state },
  { reportId, clean = false }
) => {
  const useHermes = state.hermesActive

  if (useHermes) {
    try {
      const response = await hermesCompileReport(reportId, {
        clean,
      })

      // eslint-disable-next-line no-console
      console.log('[Hermes] Compilation response:', response)

      // Handle response based on status
      if (response.status === 'current') {
        // Report is already up-to-date, no need to wait
        // eslint-disable-next-line no-console
        console.log('[Hermes] Report is current, no compilation needed')
        return response
      }

      if (response.status === 'queued' || response.status === 'in_progress') {
        // Wait for compilation to complete before returning
        // eslint-disable-next-line no-console
        console.log(
          '[Hermes] Waiting for job completion, jobId:',
          response.jobId
        )

        await waitForHermesJobCompletion(response.jobId)
        // eslint-disable-next-line no-console
        console.log('[Hermes] Job completed successfully')
      }

      return response
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Hermes] Compilation failed:', error.message)
      dispatch(
        'notifier/show',
        { type: 'Error', msg: 'Sync failed. Please try again.' },
        { root: true }
      )
      throw error
    }
  }

  // Legacy path: gcf-compile-report
  // The GCF evaluates the Hermes flag server-side and may return a Hermes-style response
  const result = await cloudFunction('compileReport', {
    id: reportId,
    clean: forcePayloadRebuild || clean,
  })

  const gcfResponse = result.data?.result
  if (gcfResponse && typeof gcfResponse === 'object' && gcfResponse.status) {
    // eslint-disable-next-line no-console
    console.log('[GCF→Hermes] Compilation response:', gcfResponse)

    if (gcfResponse.status === 'current') {
      // eslint-disable-next-line no-console
      console.log('[GCF→Hermes] Report is current, no compilation needed')
      return gcfResponse
    }

    if (
      gcfResponse.status === 'queued' ||
      gcfResponse.status === 'in_progress'
    ) {
      // Poll the GCF compile endpoint until Hermes finishes (returns 'current')
      // This avoids needing Firestore auth for job status polling
      // eslint-disable-next-line no-console
      console.log('[GCF→Hermes] Job queued, polling GCF until current...')
      await pollGcfUntilCurrent(reportId)
    }

    return gcfResponse
  }

  return result
}

// Store unsubscribe function at module level for cleanup
let hermesJobStatusUnsubscribe = null

const GCF_POLL_INTERVAL = 3000 // 3 seconds between GCF poll attempts
const GCF_POLL_TIMEOUT = 30000 // 30 seconds max wait

/**
 * Poll the compileReport GCF until it returns status 'current' (Hermes compilation done)
 * Used for unauthenticated users who can't subscribe to Firestore job status
 * @param {string} reportId - The report UUID
 * @returns {Promise<Object>} Resolves with the 'current' response
 */
const pollGcfUntilCurrent = async (reportId) => {
  const startTime = Date.now()

  while (Date.now() - startTime < GCF_POLL_TIMEOUT) {
    await new Promise((resolve) => setTimeout(resolve, GCF_POLL_INTERVAL))

    const pollResult = await cloudFunction('compileReport', {
      id: reportId,
      clean: false,
    })

    const response = pollResult.data?.result
    // eslint-disable-next-line no-console
    console.log('[GCF→Hermes] Poll response:', response?.status || 'legacy')

    if (response && typeof response === 'object') {
      if (response.status === 'current' || response.status === 'completed') {
        // eslint-disable-next-line no-console
        console.log('[GCF→Hermes] Compilation complete')
        return response
      }
      if (response.status === 'failed') {
        throw new Error(
          `Hermes compilation failed: ${response.error || 'Unknown error'}`
        )
      }
    } else {
      // Legacy string response means compilation finished via legacy path
      return pollResult
    }
  }

  throw new Error(
    `GCF→Hermes polling timed out after ${GCF_POLL_TIMEOUT / 1000}s`
  )
}

const JOB_COMPLETION_TIMEOUT = 30000 // 30 seconds max wait for job completion

/**
 * Wait for a Hermes job to complete by subscribing to Firestore job status
 * Returns a Promise that resolves when job completes or rejects on failure/timeout
 * @param {string} jobId - The job UUID returned from Hermes API
 * @param {Object} options - Options
 * @param {number} [options.timeout=30000] - Max time to wait in ms
 * @returns {Promise<Object>} Resolves with job status when completed
 */
const waitForHermesJobCompletion = (
  jobId,
  { timeout = JOB_COMPLETION_TIMEOUT, maxRedirects = 1, _startTime } = {}
) => {
  const startTime = _startTime || Date.now()

  return new Promise((resolve, reject) => {
    let unsubscribe = null
    let timeoutId = null

    const remainingTime = timeout - (Date.now() - startTime)
    if (remainingTime <= 0) {
      reject(new Error(`Hermes job ${jobId} timed out after ${timeout}ms`))
      return
    }

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      if (unsubscribe) {
        unsubscribe()
        unsubscribe = null
      }
    }

    // Set up timeout using remaining budget
    timeoutId = setTimeout(() => {
      cleanup()
      reject(new Error(`Hermes job ${jobId} timed out after ${timeout}ms`))
    }, remainingTime)

    // Subscribe to job status
    unsubscribe = subscribeToHermesJobById(
      jobId,
      (jobStatus) => {
        // eslint-disable-next-line no-console
        console.log(
          '[Hermes] waitForHermesJobCompletion status:',
          jobStatus.status
        )

        if (jobStatus.status === 'completed') {
          cleanup()
          resolve(jobStatus)
        } else if (jobStatus.status === 'failed') {
          cleanup()

          // LOCK_HELD_ACTIVE: another job holds the lock — follow the redirect
          if (
            jobStatus.error?.code === 'LOCK_HELD_ACTIVE' &&
            jobStatus.redirectJobId &&
            jobStatus.redirectJobId !== jobId &&
            maxRedirects > 0
          ) {
            // eslint-disable-next-line no-console
            console.log(
              `[Hermes] Job ${jobId} redirected to ${jobStatus.redirectJobId}`
            )
            waitForHermesJobCompletion(jobStatus.redirectJobId, {
              timeout,
              maxRedirects: maxRedirects - 1,
              _startTime: startTime,
            })
              .then(resolve)
              .catch(reject)
            return
          }

          reject(
            new Error(
              `Hermes job ${jobId} failed: ${
                jobStatus.error?.message || 'Unknown error'
              }`
            )
          )
        }
        // For 'queued' or 'in_progress', keep waiting
      },
      (error) => {
        cleanup()
        reject(error)
      }
    )
  })
}

/**
 * Subscribe to Hermes job status updates for a specific job
 * Shows notification when compilation completes so user knows it's safe to reload
 * @param {Object} context - Vuex action context
 * @param {string} jobId - The job UUID returned from Hermes API
 */
export const subscribeToHermesJobCompletion = ({ commit, dispatch }, jobId) => {
  // Unsubscribe from any existing listener
  if (hermesJobStatusUnsubscribe) {
    hermesJobStatusUnsubscribe()
  }

  hermesJobStatusUnsubscribe = subscribeToHermesJobById(
    jobId,
    (jobStatus) => {
      // eslint-disable-next-line no-console
      console.log('[Hermes] Job status update:', jobStatus.status)

      if (jobStatus.status === 'completed') {
        // Notify user that changes are synced to server
        dispatch(
          'notifier/show',
          { type: 'Saved', msg: 'Changes synced' },
          { root: true }
        )
        dispatch('unsubscribeFromHermesJobStatus')
      } else if (jobStatus.status === 'failed') {
        // Notify user of failure
        dispatch(
          'notifier/show',
          { type: 'Error', msg: 'Sync failed. Please try again.' },
          { root: true }
        )
        dispatch('unsubscribeFromHermesJobStatus')
      }
    },
    (error) => {
      // eslint-disable-next-line no-console
      console.error('[Hermes] Job status listener error:', error)
      // Don't block on listener errors - gracefully degrade
    }
  )
}

/**
 * Unsubscribe from Hermes job status updates
 */
export const unsubscribeFromHermesJobStatus = () => {
  if (hermesJobStatusUnsubscribe) {
    hermesJobStatusUnsubscribe()
    hermesJobStatusUnsubscribe = null
  }
}

/**
 * Update report backup by triggering pre-compilation
 * Routes to Hermes when state.hermesActive is true, with fallback to legacy
 * For Hermes: fire-and-forget + Firestore listener to track completion
 * @param {Object} context - Vuex action context
 * @param {Object} options - Compilation options
 * @param {string} options.reportId - The report UUID
 * @param {boolean} [options.clean=false] - Force full rebuild
 * @returns {Promise<Object>} Compilation response
 */
export const updateReportBackup = async (
  { commit, dispatch, state },
  { reportId, clean = false }
) => {
  const useHermes = state.hermesActive

  if (useHermes) {
    try {
      // Trigger async compilation (fire-and-forget)
      const response = await hermesCompileReport(reportId, { clean })

      // eslint-disable-next-line no-console
      console.log(
        '[Hermes] Compilation triggered:',
        response.status || 'success'
      )

      // Subscribe to job completion using the jobId from response
      if (response?.jobId) {
        dispatch('subscribeToHermesJobCompletion', response.jobId)
      }

      return response
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Hermes] Pre-compilation failed:', error.message)
      dispatch('unsubscribeFromHermesJobStatus')
      dispatch(
        'notifier/show',
        { type: 'Error', msg: 'Sync failed. Please try again.' },
        { root: true }
      )
      throw error
    }
  }

  // Legacy path: gcf-preCompileReport (only when FF is disabled)
  return cloudFunction('preCompileReport', { id: reportId, clean })
}

export const updateTemplateComment = async (
  { commit, dispatch, state },
  comment
) => {
  return railsService.put(
    `/api/v1/item_attributes/${comment.template_comment_id}`,
    {
      id: comment.id,
      type: 'item_attribute',
      attributes: pick(comment, ['name', 'text']),
    }
  )
}

export const fetchReportRails = (
  { commit, dispatch, state },
  { reportId, idToken, readOnly = false }
) => {
  let params = `id_token=${idToken}&view=stats,not_show_recommended_contractors_on_reports`
  if (readOnly) params += `&read_only=true`

  return railsService
    .get(`/api/v1/public/reports/${reportId}?${params}`)
    .then((response) => {
      const report = response.data.data.attributes
      const stats = report.stats
      commit('setValue', {
        key: 'reportRails',
        value: {
          ...report,
          firestore: false,
          attributes: {
            ...report,
            image_url: report.image,
            settings: report,
            stats,
          },
        },
      })
      return response.data.data
    })
}

export const fetchReportView = ({ commit, dispatch, state }, reportViewId) => {
  const params = {}
  if (
    reportViewId === state.report.attributes.settings.report_view_id ||
    reportViewId === state.report_view_id
  ) {
    params.view = 'observations,observation_photos'
    params.include = 'report'
  }

  return new Promise((resolve, reject) => {
    railsService
      .get(
        `/api/v1/public/reports/${state.report.id}/report_views/${reportViewId}`,
        { params: params }
      )
      .then((response) => {
        commit('setValue', { key: 'reportView', value: response.data.data })
        if (response.data.included)
          commit('setValue', {
            key: 'originalReport',
            value: response.data.included[0],
          })
        resolve(response.data.data)
      })
      .catch((error) => {
        commit(
          'notifier/showError',
          "We're having trouble loading your Repair Request",
          { root: true }
        )
        errorReporter.error(error)
        reject(error)
      })
  })
}

export const fetchSampleReport = (
  { commit, dispatch, state },
  sampleReportId
) => {
  return new Promise((resolve, reject) => {
    railsService
      .get(`/api/v1/sample_reports/${sampleReportId}`)
      .then((response) => {
        commit('setValue', { key: 'sampleReport', value: response.data.data })
        resolve(response.data.data)
      })
      .catch((error) => {
        commit('notifier/showError', loadingError, { root: true })
        errorReporter.error(error)
        reject(error)
      })
  })
}

export const fetchUserProfile = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    railsService
      .get('/api/v1/user_profile')
      .then((response) => {
        commit('setValue', { key: 'userProfile', value: response.data.data })
        commit('setValue', { key: 'loggedIn', value: true })
        resolve(response.data.data)
      })
      .catch((_) => {
        // Commented out because it's an expected failure
        // captureException(error)
        commit('setValue', { key: 'loggedIn', value: false })
        resolve(true)
      })
  })
}

export const fetchViewer = ({ commit, dispatch, state }, idToken) => {
  return railsService
    .get(`/api/v1/public/reports/${state.report.id}/profiles/${idToken}`)
    .then((response) => {
      commit('setValue', { key: 'viewer', value: response.data.data })
      return response
    })
}

export const reportViewUpdateSelection = (
  { commit, dispatch, state },
  observation
) => {
  let dupeSelections = JSON.parse(JSON.stringify(state.reportViewObservations))
  if (
    state.reportViewObservations.find((o) => {
      return o.observation_id === observation.observation_id
    })
  ) {
    dupeSelections = dupeSelections.map((o) => {
      return o.observation_id === observation.observation_id ? observation : o
    })
  } else {
    dupeSelections.push(observation)
  }

  commit('setValue', { key: 'reportViewObservations', value: dupeSelections })
}

export const submitReport = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    railsService
      .put(`/api/v1/reports/${state.report.id}`, {
        attributes: { id: state.report.id, submitted_on: new Date() },
      })
      .then(async (response) => {
        // Update local state with the actual submission timestamp from the server
        const submittedOn = response?.data?.data?.attributes?.submitted_on

        const updatedReport = {
          ...state.report,
          attributes: {
            ...state.report.attributes,
            ...(submittedOn && { submitted_on: submittedOn }),
          },
        }
        commit('setValue', { key: 'report', value: updatedReport })

        dispatch(
          'notifier/show',
          { type: 'Saved', msg: 'This report has been submitted!' },
          { root: true }
        )
        resolve(response.data.data)
      })
      .catch((error) => {
        commit(
          'notifier/show',
          { type: 'Error', msg: 'There was an error submitting this report.' },
          { root: true }
        )
        errorReporter.error(error)
        reject(error)
      })
  })
}

export const updateSubmissionDate = ({ commit, state }, submissionDate) => {
  const updatedReport = {
    ...state.report,
    attributes: {
      ...state.report.attributes,
      submitted_on_formatted: submissionDate,
    },
  }
  commit('setValue', { key: 'report', value: updatedReport })
}

export const updatePdfs = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    railsService
      .put(`/api/v1/reports/${state.report.id}/update_pdf`)
      .then((response) => {
        dispatch(
          'notifier/show',
          {
            type: 'Saved',
            msg: "Your PDFs are being refreshed! They'll be available again shortly.",
          },
          { root: true }
        )
        const updateReport = state.report
        updateReport.attributes.pdf_started_at =
          response.data.data.attributes.pdf_started_at
        updateReport.attributes.summary_pdf_started_at =
          response.data.data.attributes.summary_pdf_started_at

        commit('setValue', { key: 'report', value: updateReport })
      })
      .catch((error) => {
        commit('notifier/showError', 'There was an error refreshing your PDFs')
        errorReporter.error(error)
        reject(error)
      })
  })
}

export const removeObservation = (
  { commit, dispatch, state },
  observationId
) => {
  let obs = state.observations
  obs = obs.filter((o) => o.id !== observationId)
  commit('setValue', { key: 'observations', value: obs })
  dispatch('setDefectCounts')
}

export const setReinspection = ({ commit, dispatch, state }, reinspection) => {
  commit('setValue', { key: 'reinspection', value: reinspection })
}

export const setReportViewID = ({ commit, dispatch, state }, reportViewId) => {
  commit('setValue', { key: 'report_view_id', value: reportViewId })
}

export const setDefectCounts = ({ commit, dispatch, state }) => {
  const dc = {
    full: {},
    summary: {},
    hazards: {},
    all: {},
    rh: {},
    resolved: [],
    notResolved: [],
    unsatisfactory: [],
  }
  state.sections.forEach((s) => {
    Object.keys(dc).forEach((k) => {
      dc[k][s.id] = []
    })
  })

  const summaryCategories = []
  if (state.report.attributes.settings.category_high_in_summary)
    summaryCategories.push(1)
  if (state.report.attributes.settings.category_low_in_summary)
    summaryCategories.push(-1)
  if (state.report.attributes.settings.category_med_in_summary)
    summaryCategories.push(0)

  state.observations.forEach((o) => {
    if (o.attributes.comment_type === 'defect') {
      const item = state.items.find((i) => {
        return i.id === o.attributes.report_item_id
      })
      if (item) {
        dc.full[item.attributes.report_section_id].push(o.id)
        dc.all[item.attributes.report_section_id].push(o.id)
        if (summaryCategories.includes(o.attributes.category))
          dc.summary[item.attributes.report_section_id].push(o.id)
        if (o.attributes.category === 1)
          dc.hazards[item.attributes.report_section_id].push(o.id)
        if ([1, 0].includes(o.attributes.category))
          dc.rh[item.attributes.report_section_id].push(o.id)
        if (o.attributes.category === 2)
          dc.resolved[item.attributes.report_section_id].push(o.id)
        if (o.attributes.category === 3)
          dc.unsatisfactory[item.attributes.report_section_id].push(o.id)
        if (o.attributes.category === 4)
          dc.notResolved[item.attributes.report_section_id].push(o.id)
      }
    }
  })

  commit('setValue', { key: 'defectCounts', value: dc })
}

export const validateAccessToken = async (
  { commit, dispatch, state },
  accessToken
) => {
  return new Promise((resolve, reject) => {
    railsService
      .post(`/api/v1/public/reports/${state.report.id}/validate_access_token`, {
        report_access_token: accessToken,
      })
      .then((response) => {
        commit('setAccessTokenValid', true)
        resolve(response.data.data)
      })
      .catch((error) => {
        reject(error)
      })
  })
}
